/**
 * Export Button Component
 *
 * PDF/CSV export button with chart selection modal.
 * Uses the backend PDF and CSV generation services.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Download, X, FileText, Check, Loader, AlertCircle, FileSpreadsheet, Pin, ChevronDown, ChevronUp } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { useTranslation } from '../hooks/useTranslation';
import { appConfig } from '../config/AppConfig';
import { usePinnedCharts } from '../hooks/usePinnedCharts';

const ExportButtonStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(27, 140, 60, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(27, 140, 60, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.3s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
`;

const ModalTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
`;

const SelectAllButton = styled.button`
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
    border-color: #1B8C3C;
    color: #1B8C3C;
  }
`;

const ChartList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChartItem = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.checked ? '#f1f7f2' : '#f8fafc'};
  border: 1px solid ${props => props.checked ? '#1B8C3C' : 'transparent'};

  &:hover {
    background: ${props => props.checked ? '#e3f0e6' : '#f1f5f9'};
  }
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${props => props.checked ? '#1B8C3C' : '#cbd5e1'};
  background: ${props => props.checked ? '#1B8C3C' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
`;

const ChartName = styled.span`
  flex: 1;
  font-size: 14px;
  color: #1e293b;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 10px;
  margin-top: 16px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const SectionBadge = styled.span`
  background: ${props => props.color || '#1B8C3C'};
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
`;

const SubChartItem = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  padding-left: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.checked ? '#fef3c7' : '#fffbeb'};
  border: 1px solid ${props => props.checked ? '#f59e0b' : 'transparent'};
  margin-left: 16px;

  &:hover {
    background: ${props => props.checked ? '#fde68a' : '#fef3c7'};
  }
`;

const EmptyPinnedState = styled.div`
  padding: 16px;
  margin-left: 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const ExportConfirmButton = styled.button`
  flex: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: ${props => props.disabled ? '#94a3b8' : 'linear-gradient(135deg, #1B8C3C 0%, #006200 100%)'};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  background: ${props => props.error ? '#fef2f2' : '#f0fdf4'};
  color: ${props => props.error ? '#dc2626' : '#15803d'};
  font-size: 14px;
`;

const Spinner = styled(Loader)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const FormatSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 10px;
`;

const FormatButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#1e293b' : '#64748b'};
  box-shadow: ${props => props.active ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    color: #1e293b;
  }
`;

// Available charts for export — keys MUST match the dashboard chart keys from
// the orch's build_dashboard_charts() (else the backend filters them all out).
const AVAILABLE_CHARTS = [
  { key: 'complaints_by_region', en: 'Complaints by Region', ar: 'شكاوى مقدم الخدمة حسب المنطقة' },
  { key: 'complaints_by_category', en: 'Complaints by Category', ar: 'شكاوى مقدم الخدمة حسب الفئة' },
  { key: 'complaints_by_channel', en: 'Complaints by Channel', ar: 'شكاوى مقدم الخدمة حسب القناة' },
  { key: 'complaints_daily', en: 'Complaints by Date', ar: 'شكاوى مقدم الخدمة حسب التاريخ' },
  { key: 'outages_by_region', en: 'Outages by Region', ar: 'الانقطاعات حسب المنطقة' },
  { key: 'outages_by_status', en: 'Outages by Status', ar: 'الانقطاعات حسب الحالة' },
  { key: 'outages_by_priority', en: 'Outages by Priority', ar: 'الانقطاعات حسب الأولوية' },
  { key: 'connections_by_type', en: 'Connection Applications by Type', ar: 'طلبات التوصيل حسب النوع' },
  { key: 'connection_status', en: 'Connection Status Distribution', ar: 'توزيع حالة التوصيل' },
  { key: 'social_by_category', en: 'Social Cases by Category', ar: 'حالات التواصل الاجتماعي حسب الفئة' },
  { key: 'social_by_region', en: 'Social Cases by Region', ar: 'حالات التواصل الاجتماعي حسب المنطقة' },
  { key: 'sera_by_answer', en: 'SERA Complaints — Resolution', ar: 'شكاوى سيرا — المعالجة' },
  { key: 'sera_by_type', en: 'SERA Complaints by Case Type', ar: 'شكاوى سيرا حسب نوع الحالة' },
  { key: 'billing_events_by_status', en: 'Billing Events by Status', ar: 'أحداث الفوترة حسب الحالة' },
];

function ExportButton() {
  const { topN, language, datePreset, startDate, endDate } = useSettings();
  const { t } = useTranslation();
  const { pinnedCharts, loading: pinnedChartsLoading } = usePinnedCharts();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCharts, setSelectedCharts] = useState(
    AVAILABLE_CHARTS.map(c => c.key)
  );
  const [selectedPinnedCharts, setSelectedPinnedCharts] = useState([]);
  const [isPinnedSectionExpanded, setIsPinnedSectionExpanded] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [status, setStatus] = useState(null);
  const [exportFormat, setExportFormat] = useState('pdf'); // 'pdf' or 'csv'

  // Update selected pinned charts when pinnedCharts changes
  useEffect(() => {
    if (pinnedCharts.length > 0) {
      setSelectedPinnedCharts(pinnedCharts.map(c => c.id));
    }
  }, [pinnedCharts]);

  const handleOpenModal = () => {
    setIsOpen(true);
    setStatus(null);
  };

  const handleCloseModal = () => {
    if (!isExporting) {
      setIsOpen(false);
    }
  };

  const toggleChart = (chartKey) => {
    setSelectedCharts(prev =>
      prev.includes(chartKey)
        ? prev.filter(k => k !== chartKey)
        : [...prev, chartKey]
    );
  };

  const togglePinnedChart = (chartId) => {
    setSelectedPinnedCharts(prev =>
      prev.includes(chartId)
        ? prev.filter(id => id !== chartId)
        : [...prev, chartId]
    );
  };

  const selectAll = () => {
    setSelectedCharts(AVAILABLE_CHARTS.map(c => c.key));
    setSelectedPinnedCharts(pinnedCharts.map(c => c.id));
  };

  const deselectAll = () => {
    setSelectedCharts([]);
    setSelectedPinnedCharts([]);
  };

  const selectAllPinned = () => {
    setSelectedPinnedCharts(pinnedCharts.map(c => c.id));
  };

  const deselectAllPinned = () => {
    setSelectedPinnedCharts([]);
  };

  const handleExport = async () => {
    if (selectedCharts.length === 0 && selectedPinnedCharts.length === 0) return;

    setIsExporting(true);
    setStatus(null);

    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();

      // Build query params
      const params = new URLSearchParams({
        db_id: 'sera_postgres',
        lang: language,
        top_n: topN.toString()
      });

      if (datePreset && datePreset !== 'all') {
        params.append('date_preset', datePreset);
      }

      if (datePreset === 'custom' && startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }

      // Prepare pinned charts data for export
      const pinnedChartsToExport = pinnedCharts
        .filter(c => selectedPinnedCharts.includes(c.id))
        .map(c => ({
          id: c.id,
          title: c.title,
          chart_spec: c.chart_spec,
          sql_query: c.sql_query,
          original_question: c.original_question
        }));

      // Make POST request with charts in body
      const endpoint = exportFormat === 'csv' ? 'csv' : 'pdf';
      const response = await fetch(`${apiUrl}/dashboard/export/${endpoint}?${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          charts: selectedCharts,
          pinned_charts: pinnedChartsToExport
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Export failed: ${response.status}`);
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      a.download = exportFormat === 'csv'
        ? `sera_dashboard_${dateStr}.csv`
        : `sera_dashboard_${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      setStatus({ success: true, message: t('export.success') });

      // Close modal after successful export
      setTimeout(() => {
        setIsOpen(false);
        setStatus(null);
      }, 2000);

    } catch (error) {
      console.error('Export error:', error);
      setStatus({ success: false, message: error.message || t('export.error') });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <ExportButtonStyled onClick={handleOpenModal}>
        <Download size={16} />
        {t('export.title')}
      </ExportButtonStyled>

      <Overlay isOpen={isOpen} onClick={handleCloseModal}>
        <Modal isOpen={isOpen} onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              <FileText size={20} />
              {t('export.title')}
            </ModalTitle>
            <CloseButton onClick={handleCloseModal} disabled={isExporting}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            {status && (
              <StatusMessage error={!status.success}>
                {status.success ? <Check size={18} /> : <AlertCircle size={18} />}
                {status.message}
              </StatusMessage>
            )}

            <FormatSelector>
              <FormatButton
                active={exportFormat === 'pdf'}
                onClick={() => setExportFormat('pdf')}
              >
                <FileText size={16} />
                PDF
              </FormatButton>
              <FormatButton
                active={exportFormat === 'csv'}
                onClick={() => setExportFormat('csv')}
              >
                <FileSpreadsheet size={16} />
                CSV
              </FormatButton>
            </FormatSelector>

            <SelectAllRow>
              <span style={{ color: '#64748b', fontSize: '14px' }}>
                {t('export.selectCharts')}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <SelectAllButton onClick={selectAll}>
                  {t('export.selectAll')}
                </SelectAllButton>
                <SelectAllButton onClick={deselectAll}>
                  {t('export.deselectAll')}
                </SelectAllButton>
              </div>
            </SelectAllRow>

            <ChartList>
              {AVAILABLE_CHARTS.map(chart => (
                <ChartItem
                  key={chart.key}
                  checked={selectedCharts.includes(chart.key)}
                >
                  <Checkbox checked={selectedCharts.includes(chart.key)}>
                    {selectedCharts.includes(chart.key) && (
                      <Check size={14} color="white" />
                    )}
                  </Checkbox>
                  <input
                    type="checkbox"
                    checked={selectedCharts.includes(chart.key)}
                    onChange={() => toggleChart(chart.key)}
                    style={{ display: 'none' }}
                  />
                  <ChartName>{language === 'ar' ? chart.ar : chart.en}</ChartName>
                </ChartItem>
              ))}
            </ChartList>

            {/* Pinned Charts Section */}
            <SectionHeader onClick={() => setIsPinnedSectionExpanded(!isPinnedSectionExpanded)}>
              <SectionTitle>
                <Pin size={16} color="#f59e0b" />
                {language === 'ar' ? 'الرسوم البيانية المثبتة' : 'Pinned Charts'}
                <SectionBadge color="#f59e0b">{pinnedCharts.length}</SectionBadge>
              </SectionTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {pinnedCharts.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                    <SelectAllButton onClick={selectAllPinned} style={{ padding: '4px 8px', fontSize: '11px' }}>
                      {language === 'ar' ? 'الكل' : 'All'}
                    </SelectAllButton>
                    <SelectAllButton onClick={deselectAllPinned} style={{ padding: '4px 8px', fontSize: '11px' }}>
                      {language === 'ar' ? 'لا شيء' : 'None'}
                    </SelectAllButton>
                  </div>
                )}
                {isPinnedSectionExpanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
              </div>
            </SectionHeader>

            {isPinnedSectionExpanded && (
              <ChartList>
                {pinnedChartsLoading ? (
                  <EmptyPinnedState>
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    {' '}{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                  </EmptyPinnedState>
                ) : pinnedCharts.length === 0 ? (
                  <EmptyPinnedState>
                    {language === 'ar'
                      ? 'لا توجد رسوم بيانية مثبتة. قم بتثبيت الرسوم البيانية من المحادثات لتظهر هنا.'
                      : 'No pinned charts. Pin charts from conversations to see them here.'}
                  </EmptyPinnedState>
                ) : (
                  pinnedCharts.map(chart => (
                    <SubChartItem
                      key={chart.id}
                      checked={selectedPinnedCharts.includes(chart.id)}
                    >
                      <Checkbox checked={selectedPinnedCharts.includes(chart.id)}>
                        {selectedPinnedCharts.includes(chart.id) && (
                          <Check size={14} color="white" />
                        )}
                      </Checkbox>
                      <input
                        type="checkbox"
                        checked={selectedPinnedCharts.includes(chart.id)}
                        onChange={() => togglePinnedChart(chart.id)}
                        style={{ display: 'none' }}
                      />
                      <ChartName style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                        {chart.title || chart.original_question?.substring(0, 50) || 'Untitled Chart'}
                      </ChartName>
                    </SubChartItem>
                  ))
                )}
              </ChartList>
            )}
          </ModalContent>

          <ModalFooter>
            <CancelButton onClick={handleCloseModal} disabled={isExporting}>
              {t('export.cancel')}
            </CancelButton>
            <ExportConfirmButton
              onClick={handleExport}
              disabled={isExporting || (selectedCharts.length === 0 && selectedPinnedCharts.length === 0)}
            >
              {isExporting ? (
                <>
                  <Spinner size={16} />
                  {t('export.generating')}
                </>
              ) : (
                <>
                  <Download size={16} />
                  {exportFormat === 'csv' ? 'CSV' : 'PDF'}
                </>
              )}
            </ExportConfirmButton>
          </ModalFooter>
        </Modal>
      </Overlay>
    </>
  );
}

export default ExportButton;
